import React, { useState, useEffect } from "react";
import {
  Search,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Star,
  Briefcase,
  Upload,
  X,
} from "lucide-react";
import AdminLayout from "../common/AdminLayout";
import Loader from "../common/Loader";
import api, { API_BASE_URL } from "../../utils/api";
import styles from "./Services.module.css";

interface Service {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  image: string;
  galleryImages?: string[];
  portfolio?: string[];
  createdAt: string;
  worker: {
    id: number;
    name: string;
    profilePicture: string;
    verificationStatus: string;
    rating: number;
  };
  isActive: boolean;
  approval_status?: "pending" | "approved" | "rejected";
}

interface PendingService {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  image: string;
  created_at: string;
  worker_id: number;
  first_name: string;
  last_name: string;
  worker_email: string;
  worker_phone: string;
  parish: string;
  rating: number;
  category_created_at: string;
  category_created_by: number;
}

const getImageUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const base = API_BASE_URL.replace(/\/api\/?$/, "");
  if (path.startsWith("/uploads/") || path.startsWith("uploads/")) {
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    return `${base}/${cleanPath}`;
  }
  return `${base}/uploads/${path}`;
};

// ── Inline toast for success/error feedback ──────────────────────────────────
const Toast: React.FC<{ message: string; type: "success" | "error"; onClose: () => void }> = ({
  message, type, onClose,
}) => (
  <div
    style={{
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: 99999,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 20px",
      borderRadius: "8px",
      background: type === "success" ? "#10b981" : "#ef4444",
      color: "white",
      fontWeight: 500,
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      minWidth: "280px",
      animation: "slideIn 0.3s ease",
    }}
  >
    {type === "success" ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
    <span style={{ flex: 1 }}>{message}</span>
    <button
      onClick={onClose}
      style={{ background: "none", border: "none", color: "white", cursor: "pointer", padding: 0 }}
    >
      <X size={16} />
    </button>
  </div>
);

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [pendingServices, setPendingServices] = useState<PendingService[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);

  // ── Error / success state ──────────────────────────────────────────────────
  const [pageError, setPageError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Filters ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categories, setCategories] = useState<string[]>([]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // ── Selection ──────────────────────────────────────────────────────────────
  const [selectedServices, setSelectedServices] = useState<number[]>([]);

  // ── Modal ──────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [currentService, setCurrentService] = useState<Service | null>(null);
  const [modalType, setModalType] = useState<"view" | "edit" | "delete" | "add">("view");

  // ── Add form ───────────────────────────────────────────────────────────────
  const [newService, setNewService] = useState({
    worker_id: 0,
    title: "",
    description: "",
    price: "",
    duration: "",
    category: "",
    is_active: true,
    image: null as File | null,
  });
  const [workers, setWorkers] = useState<{ id: number; name: string }[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Edit form ──────────────────────────────────────────────────────────────
  const [editService, setEditService] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    categoryId: null as number | null,
    isActive: true,
  });
  const [editCategories, setEditCategories] = useState<{ id: number; name: string }[]>([]);
  const [editMainImageFile, setEditMainImageFile] = useState<File | null>(null);
  const [editMainImagePreview, setEditMainImagePreview] = useState<string | null>(null);
  const [editGalleryFiles, setEditGalleryFiles] = useState<File[]>([]);
  const [editGalleryPreviews, setEditGalleryPreviews] = useState<string[]>([]);
  const [galleryMode, setGalleryMode] = useState<"replace" | "append">("replace");
  const [editPortfolioFiles, setEditPortfolioFiles] = useState<File[]>([]);
  const [editPortfolioPreviews, setEditPortfolioPreviews] = useState<string[]>([]);
  const [portfolioMode, setPortfolioMode] = useState<"replace" | "append">("replace");
  const [serviceDetails, setServiceDetails] = useState<any | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchServices();
    fetchCategories();
    fetchPendingServices();
  }, []);

  useEffect(() => {
    if (modalType === "add") fetchWorkers();
  }, [modalType]);

  useEffect(() => {
    if (modalType === "edit" && currentService && editCategories.length > 0) {
      const match = editCategories.find((c) => c.name === currentService.category);
      setEditService((prev) => ({ ...prev, categoryId: match ? match.id : null }));
    }
  }, [modalType, currentService, editCategories]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchServices = async () => {
    try {
      setLoading(true);
      setPageError(null);
      const response = await api.get("/api/admin/services");
      if (response.success) {
        setServices(response.services);
      } else {
        throw new Error(response.message || "Failed to fetch services");
      }
    } catch (error: any) {
      setPageError(error.response?.data?.message || error.message || "Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingServices = async () => {
    try {
      setLoadingPending(true);
      const response = await api.get("/api/admin/services/pending");
      if (response.success) setPendingServices(response.services || []);
    } catch (error) {
      console.error("Error fetching pending services:", error);
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/api/services/categories");
      if (response.success) setCategories(response.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await api.get("/api/admin/service-providers");
      if (response.success) {
        setWorkers(
          response.providers.map((p: any) => ({
            id: p.id,
            name: `${p.firstName} ${p.lastName}`,
          }))
        );
      }
    } catch (error) {
      setModalError("Failed to load service providers. Please try again.");
    }
  };

  const fetchEditCategories = async () => {
    try {
      const response = await api.get("/api/categories/dropdown");
      if (response.success) {
        setEditCategories(response.categories.map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (err) {
      console.error("Error fetching categories for edit:", err);
    }
  };

  const fetchServiceDetails = async (id: number) => {
    try {
      const resp = await api.get(`/api/admin/services/${id}`);
      if (resp.success) setServiceDetails(resp.service);
    } catch (err) {
      console.error("Failed to fetch service details:", err);
    }
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────
  const handleApproveService = async (serviceId: number) => {
    if (!window.confirm("Are you sure you want to approve this service?")) return;
    try {
      const response = await api.put(`/api/admin/services/${serviceId}/approve`, {});
      if (response.success) {
        showToast("Service approved successfully!");
        fetchServices();
        fetchPendingServices();
      } else {
        throw new Error(response.message || "Failed to approve service");
      }
    } catch (error: any) {
      showToast(error.message || "Failed to approve service", "error");
    }
  };

  const handleRejectService = async (serviceId: number) => {
    const reason = window.prompt("Please provide a reason for rejection (optional):");
    if (reason === null) return;
    try {
      const response = await api.put(`/api/admin/services/${serviceId}/reject`, { reason });
      if (response.success) {
        showToast("Service rejected successfully!");
        fetchServices();
        fetchPendingServices();
      } else {
        throw new Error(response.message || "Failed to reject service");
      }
    } catch (error: any) {
      showToast(error.message || "Failed to reject service", "error");
    }
  };

  // ── Filtering — frontend, instant, no API call ─────────────────────────────
  const filteredServices = services.filter((service) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      service.title.toLowerCase().includes(q) ||
      service.description.toLowerCase().includes(q) ||
      service.worker.name.toLowerCase().includes(q) ||
      service.category.toLowerCase().includes(q) ||
      String(service.price).includes(q) ||
      service.duration.toLowerCase().includes(q);

    const matchesCategory =
      categoryFilter === "all" || service.category === categoryFilter;

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && service.isActive) ||
      (statusFilter === "inactive" && !service.isActive);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentServices = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  // ── Export ─────────────────────────────────────────────────────────────────
  const exportServices = () => {
    if (filteredServices.length === 0) {
      showToast("No services to export", "error");
      return;
    }
    const csvContent = [
      ["ID", "Title", "Description", "Category", "Price", "Duration", "Provider", "Status", "Created At"].join(","),
      ...filteredServices.map((s) =>
        [
          s.id,
          `"${s.title.replace(/"/g, '""')}"`,
          `"${s.description.replace(/"/g, '""')}"`,
          `"${s.category}"`,
          s.price,
          `"${s.duration}"`,
          `"${s.worker.name}"`,
          s.isActive ? "Active" : "Inactive",
          new Date(s.createdAt).toLocaleDateString(),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `services_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${filteredServices.length} services`);
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const toggleServiceSelection = (serviceId: number) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleBulkAction = async (action: "activate" | "deactivate" | "delete") => {
    if (selectedServices.length === 0) return;
    try {
      const response = await api.patch("/api/admin/services/bulk", {
        serviceIds: selectedServices,
        action,
      });
      if (response.success) {
        setSelectedServices([]);
        fetchServices();
        showToast(`Bulk ${action} successful`);
      } else {
        throw new Error(response.message || `Failed to perform bulk ${action}`);
      }
    } catch (error: any) {
      showToast(error.message || `Failed to perform bulk ${action}`, "error");
    }
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openModal = (
    service: Service | null,
    type: "view" | "edit" | "delete" | "add"
  ) => {
    setCurrentService(service);
    setModalType(type);
    setModalError(null);
    setShowModal(true);

    if (type === "add") {
      setNewService({
        worker_id: 0,
        title: "",
        description: "",
        price: "",
        duration: "",
        category: "",
        is_active: true,
        image: null,
      });
      setImagePreview(null);
    } else if (type === "edit" && service) {
      setEditService({
        title: service.title,
        description: service.description,
        price: String(service.price),
        duration: service.duration,
        categoryId: null,
        isActive: service.isActive,
      });
      fetchEditCategories();
      fetchServiceDetails(service.id);
      setEditMainImageFile(null);
      setEditMainImagePreview(null);
      setEditGalleryFiles([]);
      setEditGalleryPreviews([]);
      setGalleryMode("replace");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentService(null);
    setServiceDetails(null);
    setModalError(null);
    setEditMainImageFile(null);
    setEditMainImagePreview(null);
    setEditGalleryFiles([]);
    setEditGalleryPreviews([]);
  };

  // ── Form handlers ──────────────────────────────────────────────────────────
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewService((prev) => ({
      ...prev,
      [name]: name === "worker_id" ? parseInt(value) : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewService((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewService((prev) => ({ ...prev, image: file }));
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "categoryId") {
      setEditService((prev) => ({ ...prev, categoryId: value ? parseInt(value) : null }));
    } else {
      setEditService((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "isActive") {
      setEditService((prev) => ({ ...prev, isActive: e.target.checked }));
    }
  };

  const handleEditMainImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditMainImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setEditMainImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleEditGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? (Array.from(e.target.files) as File[]) : [];
    if (!files.length) {
      setEditGalleryFiles([]);
      setEditGalleryPreviews([]);
      return;
    }
    setEditGalleryFiles(files);
    Promise.all(
      files.map(
        (f) =>
          new Promise<string>((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result as string);
            r.readAsDataURL(f);
          })
      )
    ).then(setEditGalleryPreviews);
  };

  const handleEditPortfolioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? (Array.from(e.target.files) as File[]) : [];
    if (!files.length) {
      setEditPortfolioFiles([]);
      setEditPortfolioPreviews([]);
      return;
    }
    setEditPortfolioFiles(files);
    Promise.all(
      files.map(
        (f) =>
          new Promise<string>((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result as string);
            r.readAsDataURL(f);
          })
      )
    ).then(setEditPortfolioPreviews);
  };

  // ── CRUD operations ────────────────────────────────────────────────────────
  const handleCreateService = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setModalError(null);

    if (
      !newService.worker_id ||
      !newService.title ||
      !newService.description ||
      !newService.price ||
      !newService.duration ||
      !newService.category
    ) {
      setModalError("Please fill in all required fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("worker_id", newService.worker_id.toString());
      formData.append("title", newService.title);
      formData.append("description", newService.description);
      formData.append("price", newService.price);
      formData.append("duration", newService.duration);
      formData.append("category", newService.category);
      formData.append("is_active", newService.is_active.toString());
      if (newService.image) formData.append("image", newService.image);

      const result = await api.postFormData("/api/admin/services", formData);
      if (result.success) {
        closeModal();
        fetchServices();
        showToast("Service created successfully!");
      } else {
        throw new Error(result.message || "Failed to create service");
      }
    } catch (error: any) {
      setModalError(error.message || "Failed to create service");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateService = async () => {
    if (!currentService) return;
    setModalError(null);
    try {
      setIsUpdating(true);
      const payload: any = {};
      if (editService.title) payload.title = editService.title;
      if (editService.description) payload.description = editService.description;
      if (editService.price) payload.price = parseFloat(editService.price);
      if (editService.duration) payload.duration = editService.duration;
      if (editService.categoryId !== null) payload.category_id = editService.categoryId;
      payload.isActive = editService.isActive;

      const response = await api.put(`/api/admin/services/${currentService.id}`, payload);
      if (response.success) {
        closeModal();
        fetchServices();
        showToast("Service updated successfully!");
      } else {
        throw new Error(response.message || "Failed to update service");
      }
    } catch (err: any) {
      setModalError(err.message || "Failed to update service");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUploadImages = async () => {
    if (!currentService) return;
    if (!editMainImageFile && editGalleryFiles.length === 0 && editPortfolioFiles.length === 0) {
      setModalError("Please select an image, gallery, or portfolio files to upload.");
      return;
    }
    try {
      setIsUploadingImages(true);
      setModalError(null);
      const form = new FormData();
      if (editMainImageFile) form.append("image", editMainImageFile);
      for (const gf of editGalleryFiles) form.append("gallery_images", gf);
      for (const pf of editPortfolioFiles) form.append("portfolio", pf);

      const resp = await api.postFormData(
        `/api/admin/services/${currentService.id}/images?mode=${galleryMode}`,
        form
      );
      if (resp.success) {
        await fetchServiceDetails(currentService.id);
        await fetchServices();
        setEditMainImageFile(null);
        setEditMainImagePreview(null);
        setEditGalleryFiles([]);
        setEditGalleryPreviews([]);
        setEditPortfolioFiles([]);
        setEditPortfolioPreviews([]);
        showToast("Images updated successfully!");
      } else {
        throw new Error(resp.message || "Failed to update images");
      }
    } catch (err: any) {
      setModalError(err.message || "Failed to update images");
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleDeleteService = async () => {
    if (!currentService) return;
    setModalError(null);
    try {
      const response = await api.delete(`/api/admin/services/${currentService.id}`);
      if (response.success) {
        closeModal();
        fetchServices();
        showToast("Service deleted successfully!");
      } else {
        throw new Error(response.message || "Failed to delete service");
      }
    } catch (error: any) {
      setModalError(error.message || "Failed to delete service");
    }
  };

  // ── Formatters ─────────────────────────────────────────────────────────────
  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading && !services.length) {
    return (
      <AdminLayout title="Service Management">
        <Loader message="Loading services..." />
      </AdminLayout>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <AdminLayout
      title="Service Management"
      subtitle="Manage all services offered on the platform"
    >
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="fade-in">
        {/* ── Header Actions ── */}
        <div className="admin-card" style={{ marginBottom: "25px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
              {/* Search */}
              <div style={{ position: "relative", minWidth: "300px" }}>
                <input
                  type="text"
                  placeholder="Search by title, provider, category..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "40px" }}
                />
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-dark-gray)",
                  }}
                />
              </div>

              {/* Category filter */}
              <select
                className="form-control"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ minWidth: "140px" }}
              >
                <option value="all">All Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              {/* Status filter */}
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ minWidth: "140px" }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              {/* ✅ Export button — now wired up */}
              <button className="btn-secondary" onClick={exportServices}>
                <Download size={16} />
                <span>Export</span>
              </button>
              <button className="btn-primary" onClick={() => openModal(null, "add")}>
                <Plus size={16} />
                <span>Add Service</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Page-level error ── */}
        {pageError && (
          <div
            className="alert alert-danger"
            style={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <AlertTriangle size={18} />
            {pageError}
            <button
              onClick={() => setPageError(null)}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "inherit",
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* ── Pending Services ── */}
        {pendingServices.length > 0 && (
          <div
            className="admin-card"
            style={{ marginBottom: "25px", border: "2px solid var(--accent-orange)" }}
          >
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <AlertTriangle size={24} color="var(--accent-orange)" />
              <h3 style={{ margin: 0, color: "var(--text-white)" }}>
                Pending Approvals ({pendingServices.length})
              </h3>
            </div>
            <div style={{ display: "grid", gap: "15px" }}>
              {pendingServices.map((service) => {
                const serviceImgUrl = getImageUrl(service.image);
                return (
                  <div
                    key={service.id}
                    style={{
                      padding: "20px",
                      background: "var(--bg-secondary)",
                      borderRadius: "8px",
                      border: "1px solid var(--bg-tertiary)",
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: "20px",
                      alignItems: "center",
                    }}
                  >
                    {serviceImgUrl ? (
                      <img
                        src={serviceImgUrl}
                        alt={service.title}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          background: "var(--bg-light)",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Briefcase size={32} color="var(--text-medium)" />
                      </div>
                    )}
                    <div>
                      <h4 style={{ margin: "0 0 8px 0", color: "var(--text-white)" }}>
                        {service.title}
                      </h4>
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          color: "var(--text-medium)",
                          fontSize: "14px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {service.description}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "15px",
                          flexWrap: "wrap",
                          fontSize: "13px",
                        }}
                      >
                        <span style={{ color: "var(--text-medium)" }}>
                          Provider:{" "}
                          <span style={{ color: "var(--text-light)" }}>
                            {service.first_name} {service.last_name}
                          </span>
                        </span>
                        <span style={{ color: "var(--text-medium)" }}>
                          Price:{" "}
                          <span style={{ color: "var(--accent-green)" }}>${service.price}</span>
                        </span>
                        <span style={{ color: "var(--text-medium)" }}>
                          Category:{" "}
                          <span
                            style={{
                              padding: "3px 8px",
                              background: "var(--accent-orange)",
                              borderRadius: "12px",
                              fontSize: "12px",
                              color: "white",
                            }}
                          >
                            {service.category} (New)
                          </span>
                        </span>
                        <span style={{ color: "var(--text-medium)" }}>
                          Duration:{" "}
                          <span style={{ color: "var(--text-light)" }}>{service.duration}</span>
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        onClick={() => handleApproveService(service.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "10px 16px",
                          background: "var(--accent-green)",
                          border: "none",
                          borderRadius: "6px",
                          color: "white",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <CheckCircle size={18} />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleRejectService(service.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "10px 16px",
                          background: "var(--accent-red)",
                          border: "none",
                          borderRadius: "6px",
                          color: "white",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <XCircle size={18} />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Services Table ── */}
        <div className="admin-card">
          <div className="card-header" style={{ marginBottom: "15px" }}>
            <div className="card-title">Services</div>
            <div style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}>
              Showing {Math.min(indexOfFirstItem + 1, filteredServices.length)}–
              {Math.min(indexOfLastItem, filteredServices.length)} of{" "}
              {filteredServices.length} services
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={
                        selectedServices.length === currentServices.length &&
                        currentServices.length > 0
                      }
                      onChange={() => {
                        if (selectedServices.length === currentServices.length) {
                          setSelectedServices([]);
                        } else {
                          setSelectedServices(currentServices.map((s) => s.id));
                        }
                      }}
                    />
                  </th>
                  <th>Service</th>
                  <th>Category</th>
                  <th>Provider</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th style={{ width: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentServices.length > 0 ? (
                  currentServices.map((service) => {
                    const serviceImgUrl = getImageUrl(service.image);
                    const profileImgUrl = getImageUrl(service.worker.profilePicture);
                    return (
                      <tr key={service.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedServices.includes(service.id)}
                            onChange={() => toggleServiceSelection(service.id)}
                          />
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {serviceImgUrl ? (
                              <img
                                src={serviceImgUrl}
                                alt={service.title}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "var(--bg-light)",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Briefcase size={20} color="var(--text-medium)" />
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: "500" }}>{service.title}</div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--text-medium)",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "200px",
                                }}
                              >
                                {service.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{service.category}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {profileImgUrl ? (
                              <img
                                src={profileImgUrl}
                                alt={service.worker.name}
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  backgroundColor: "var(--accent-blue)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                {service.worker.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <div style={{ fontSize: "14px" }}>{service.worker.name}</div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  color: "var(--text-medium)",
                                }}
                              >
                                <Star size={12} fill="gold" stroke="gold" />
                                {service.worker.rating.toFixed(1)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{formatPrice(service.price)}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              service.isActive ? "status-active" : "status-inactive"
                            }`}
                          >
                            {service.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{formatDate(service.createdAt)}</td>
                        <td>
                          <div className={styles.actionButtons}>
                            <button
                              className={`${styles.actionBtn} ${styles.viewBtn}`}
                              onClick={() => openModal(service, "view")}
                              title="View Service"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.editBtn}`}
                              onClick={() => openModal(service, "edit")}
                              title="Edit Service"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => openModal(service, "delete")}
                              title="Delete Service"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "40px 0" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "10px",
                          color: "var(--text-medium)",
                        }}
                      >
                        <Search size={32} />
                        <div style={{ fontWeight: "500" }}>No services found</div>
                        <div style={{ fontSize: "14px" }}>
                          {searchTerm
                            ? `No results for "${searchTerm}"`
                            : "Try adjusting your filters"}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredServices.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                padding: "0 10px",
              }}
            >
              <div style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}>
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: "flex", gap: "5px" }}>
                <button
                  className="btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  style={{ padding: "8px 12px" }}
                >
                  Previous
                </button>
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let start = Math.max(1, currentPage - 2);
                  let end = Math.min(totalPages, start + maxVisible - 1);
                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }
                  for (let i = start; i <= end; i++) {
                    pages.push(i);
                  }
                  return pages.map((pageNum) => (
                    <button
                      key={pageNum}
                      className="btn-secondary"
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor:
                          currentPage === pageNum ? "var(--accent-blue)" : "",
                        color: currentPage === pageNum ? "white" : "",
                      }}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                <button
                  className="btn-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  style={{ padding: "8px 12px" }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Bulk Actions bar ── */}
        {selectedServices.length > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: "30px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#1e293b",
              color: "white",
              padding: "14px 24px",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              zIndex: 99999,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap" }}>
              {selectedServices.length} selected
            </span>

            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.2)" }} />

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleBulkAction("activate")}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 14px", borderRadius: "8px", border: "none",
                  background: "#10b981", color: "white", cursor: "pointer",
                  fontSize: "13px", fontWeight: 500,
                }}
              >
                <CheckCircle size={14} /> Activate
              </button>

              <button
                onClick={() => handleBulkAction("deactivate")}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 14px", borderRadius: "8px", border: "none",
                  background: "#f59e0b", color: "white", cursor: "pointer",
                  fontSize: "13px", fontWeight: 500,
                }}
              >
                <XCircle size={14} /> Deactivate
              </button>

              <button
                onClick={() => handleBulkAction("delete")}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 14px", borderRadius: "8px", border: "none",
                  background: "#ef4444", color: "white", cursor: "pointer",
                  fontSize: "13px", fontWeight: 500,
                }}
              >
                <Trash2 size={14} /> Delete
              </button>

              <button
                onClick={() => setSelectedServices([])}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "8px 14px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.2)",
                  background: "transparent", color: "white", cursor: "pointer",
                  fontSize: "13px", fontWeight: 500,
                }}
              >
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Modal ── */}
        {showModal && (
          <div className={styles.modalOverlay} onClick={closeModal} style={{zIndex: 9999}}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>
                  {modalType === "view" && "Service Details"}
                  {modalType === "edit" && "Edit Service"}
                  {modalType === "delete" && "Delete Service"}
                  {modalType === "add" && "Add New Service"}
                </h2>
                <button className={styles.modalClose} onClick={closeModal}>
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                {/* ── Modal-level error ── */}
                {modalError && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "12px 16px",
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: "8px",
                      color: "#ef4444",
                      marginBottom: "16px",
                      fontSize: "14px",
                    }}
                  >
                    <AlertTriangle size={16} />
                    <span style={{ flex: 1 }}>{modalError}</span>
                    <button
                      onClick={() => setModalError(null)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#ef4444",
                        padding: 0,
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                {/* VIEW */}
                {modalType === "view" && currentService && (
                  <div className={styles.serviceDetails}>
                    {getImageUrl(currentService.image) && (
                      <img
                        src={getImageUrl(currentService.image)!}
                        alt={currentService.title}
                        className={styles.serviceImage}
                      />
                    )}
                    <h3>{currentService.title}</h3>
                    <p>{currentService.description}</p>
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Category:</span>
                        <span className={styles.detailValue}>{currentService.category}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Price:</span>
                        <span className={styles.detailValue}>
                          {formatPrice(currentService.price)}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Duration:</span>
                        <span className={styles.detailValue}>{currentService.duration}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Provider:</span>
                        <span className={styles.detailValue}>{currentService.worker.name}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Status:</span>
                        <span
                          className={`status-badge ${
                            currentService.isActive ? "status-active" : "status-inactive"
                          }`}
                        >
                          {currentService.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Created:</span>
                        <span className={styles.detailValue}>
                          {formatDate(currentService.createdAt)}
                        </span>
                      </div>
                    </div>
                    {currentService.galleryImages && currentService.galleryImages.length > 0 && (
                      <div style={{ marginTop: "20px", width: "100%" }}>
                        <h4 style={{ fontSize: "15px", marginBottom: "12px", fontWeight: "600", borderBottom: "1px solid var(--border-color)", paddingBottom: "6px" }}>Gallery Images ({currentService.galleryImages.length} items)</h4>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                          {currentService.galleryImages.map((img: string, idx: number) => (
                            <img
                              key={idx}
                              src={getImageUrl(img) ?? ""}
                              alt={`Gallery Image ${idx + 1}`}
                              style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "6px" }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {currentService.portfolio && currentService.portfolio.length > 0 && (
                      <div style={{ marginTop: "20px", width: "100%" }}>
                        <h4 style={{ fontSize: "15px", marginBottom: "12px", fontWeight: "600", borderBottom: "1px solid var(--border-color)", paddingBottom: "6px" }}>Portfolio ({currentService.portfolio.length} items)</h4>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                          {currentService.portfolio.map((img: string, idx: number) => {
                            const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => img.toLowerCase().endsWith(ext));
                            return isVideo ? (
                              <video
                                key={idx}
                                src={getImageUrl(img) ?? ""}
                                style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "6px" }}
                                controls
                                muted
                              />
                            ) : (
                              <img
                                key={idx}
                                src={getImageUrl(img) ?? ""}
                                alt={`Portfolio Item ${idx + 1}`}
                                style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "6px" }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* DELETE */}
                {modalType === "delete" && currentService && (
                  <div className={styles.deleteConfirmation}>
                    <AlertTriangle size={48} color="var(--danger)" style={{ marginBottom: "20px" }} />
                    <h3>Are you sure you want to delete this service?</h3>
                    <p>
                      This action cannot be undone. The service "{currentService.title}" will be
                      permanently removed.
                    </p>
                  </div>
                )}

                {/* EDIT */}
                {modalType === "edit" && currentService && (
                  <form
                    className={styles.serviceForm}
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateService();
                    }}
                  >
                    <div className={styles.formGroup}>
                      <label htmlFor="title_edit">Service Title *</label>
                      <input
                        type="text"
                        id="title_edit"
                        name="title"
                        value={editService.title}
                        onChange={handleEditInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="description_edit">Description *</label>
                      <textarea
                        id="description_edit"
                        name="description"
                        value={editService.description}
                        onChange={handleEditInputChange}
                        className="form-control"
                        rows={2}
                        required
                      />
                    </div>
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label htmlFor="price_edit">Price ($) *</label>
                        <input
                          type="number"
                          id="price_edit"
                          name="price"
                          value={editService.price}
                          onChange={handleEditInputChange}
                          className="form-control"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="duration_edit">Duration *</label>
                        <input
                          type="text"
                          id="duration_edit"
                          name="duration"
                          value={editService.duration}
                          onChange={handleEditInputChange}
                          className="form-control"
                          placeholder="e.g. 2 hours"
                          required
                        />
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="category_edit">Category</label>
                      <select
                        id="category_edit"
                        name="categoryId"
                        value={editService.categoryId ?? ""}
                        onChange={handleEditInputChange}
                        className="form-control"
                      >
                        <option value="">Select Category</option>
                        {editCategories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <div className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          id="is_active_edit"
                          name="isActive"
                          checked={editService.isActive}
                          onChange={handleEditCheckboxChange}
                        />
                        <label htmlFor="is_active_edit">Active Service</label>
                      </div>
                    </div>

                    {/* Main Image */}
                    <div className={styles.formGroup}>
                      <label>Main Image</label>
                      <div className={styles.imageUploadContainer}>
                        {editMainImagePreview ? (
                          <div className={styles.imagePreviewContainer}>
                            <img src={editMainImagePreview} alt="Main" className={styles.imagePreview} />
                            <button
                              type="button"
                              className={styles.removeImageBtn}
                              onClick={() => {
                                setEditMainImageFile(null);
                                setEditMainImagePreview(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className={styles.uploadPlaceholder}>
                            <input
                              type="file"
                              id="edit_main_image"
                              onChange={handleEditMainImageSelect}
                              accept="image/*"
                              className={styles.fileInput}
                            />
                            <label htmlFor="edit_main_image" className={styles.uploadLabel}>
                              <Upload size={24} />
                              <span>Upload Main Image</span>
                            </label>
                          </div>
                        )}
                        {!editMainImagePreview && serviceDetails?.image && (
                          <div style={{ marginTop: 10 }}>
                            <img
                              src={getImageUrl(serviceDetails.image) ?? ""}
                              alt="Current"
                              style={{
                                width: 100,
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gallery Images */}
                    <div className={styles.formGroup}>
                      <label>Gallery Images</label>
                      <div className={styles.uploadPlaceholder}>
                        <input
                          type="file"
                          id="edit_gallery"
                          multiple
                          accept="image/*"
                          onChange={handleEditGallerySelect}
                          className={styles.fileInput}
                        />
                        <label htmlFor="edit_gallery" className={styles.uploadLabel}>
                          <Upload size={24} />
                          <span>Upload Gallery Images</span>
                        </label>
                      </div>
                      {editGalleryPreviews.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                          {editGalleryPreviews.map((src, idx) => (
                            <img
                              key={idx}
                              src={src}
                              alt={`New Gallery ${idx}`}
                              style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                            />
                          ))}
                        </div>
                      )}
                      {!editGalleryPreviews.length &&
                        serviceDetails?.galleryImages?.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                            {serviceDetails.galleryImages.map((img: string, idx: number) => (
                              <img
                                key={idx}
                                src={getImageUrl(img) ?? ""}
                                alt={`Gallery ${idx}`}
                                style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                              />
                            ))}
                          </div>
                        )}
                    </div>

                    {/* Portfolio */}
                    <div className={styles.formGroup}>
                      <label>Portfolio Items (Images & Videos)</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="radio"
                            name="galleryMode"
                            value="replace"
                            checked={galleryMode === "replace"}
                            onChange={() => setGalleryMode("replace")}
                          />{" "}
                          Replace
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="radio"
                            name="galleryMode"
                            value="append"
                            checked={galleryMode === "append"}
                            onChange={() => setGalleryMode("append")}
                          />{" "}
                          Append
                        </label>
                      </div>
                      <div className={styles.uploadPlaceholder}>
                        <input
                          type="file"
                          id="edit_portfolio"
                          multiple
                          accept="image/*,video/*"
                          onChange={handleEditPortfolioSelect}
                          className={styles.fileInput}
                        />
                        <label htmlFor="edit_portfolio" className={styles.uploadLabel}>
                          <Upload size={24} />
                          <span>Upload Portfolio</span>
                        </label>
                      </div>
                      {editPortfolioPreviews.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                          {editPortfolioPreviews.map((src, idx) => {
                            const file = editPortfolioFiles[idx];
                            const isVideo = file?.type?.startsWith('video/') || 
                                            ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => file?.name?.toLowerCase().endsWith(ext));
                            return isVideo ? (
                              <video
                                key={idx}
                                src={src}
                                style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                                muted
                              />
                            ) : (
                              <img
                                key={idx}
                                src={src}
                                alt={`New Portfolio ${idx}`}
                                style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                              />
                            );
                          })}
                        </div>
                      )}
                      {!editPortfolioPreviews.length &&
                        serviceDetails?.portfolio?.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                            {serviceDetails.portfolio.map((img: string, idx: number) => {
                              const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => img.toLowerCase().endsWith(ext));
                              return isVideo ? (
                                <video
                                  key={idx}
                                  src={getImageUrl(img) ?? ""}
                                  style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                                  muted
                                />
                              ) : (
                                <img
                                  key={idx}
                                  src={getImageUrl(img) ?? ""}
                                  alt={`Portfolio ${idx}`}
                                  style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6 }}
                                />
                              );
                            })}
                          </div>
                        )}
                      <div style={{ display: "flex", gap: 10, marginTop: 12, justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setEditMainImageFile(null);
                            setEditMainImagePreview(null);
                            setEditGalleryFiles([]);
                            setEditGalleryPreviews([]);
                            setEditPortfolioFiles([]);
                            setEditPortfolioPreviews([]);
                          }}
                        >
                          Clear Selection
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleUploadImages}
                          disabled={isUploadingImages}
                        >
                          {isUploadingImages ? "Uploading..." : "Update Images"}
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {/* ADD */}
                {modalType === "add" && (
                  <form onSubmit={handleCreateService} className={styles.serviceForm}>
                    <div className={styles.formGroup}>
                      <label htmlFor="worker_id">Service Provider *</label>
                      <select
                        id="worker_id"
                        name="worker_id"
                        value={newService.worker_id || ""}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      >
                        <option value="">Select Service Provider</option>
                        {workers.map((worker) => (
                          <option key={worker.id} value={worker.id}>
                            {worker.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="title">Service Title *</label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={newService.title}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="description">Description *</label>
                      <textarea
                        id="description"
                        name="description"
                        value={newService.description}
                        onChange={handleInputChange}
                        className="form-control"
                        rows={4}
                        required
                      />
                    </div>
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label htmlFor="price">Price ($) *</label>
                        <input
                          type="number"
                          id="price"
                          name="price"
                          value={newService.price}
                          onChange={handleInputChange}
                          className="form-control"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="duration">Duration *</label>
                        <input
                          type="text"
                          id="duration"
                          name="duration"
                          value={newService.duration}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="e.g. 2 hours"
                          required
                        />
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="category">Category *</label>
                      <select
                        id="category"
                        name="category"
                        value={newService.category}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((category, index) => (
                          <option key={index} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label>Service Image</label>
                      <div className={styles.imageUploadContainer}>
                        {imagePreview ? (
                          <div className={styles.imagePreviewContainer}>
                            <img
                              src={imagePreview}
                              alt="Service Preview"
                              className={styles.imagePreview}
                            />
                            <button
                              type="button"
                              className={styles.removeImageBtn}
                              onClick={() => {
                                setNewService((prev) => ({ ...prev, image: null }));
                                setImagePreview(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className={styles.uploadPlaceholder}>
                            <input
                              type="file"
                              id="image"
                              name="image"
                              onChange={handleImageUpload}
                              accept="image/*"
                              className={styles.fileInput}
                            />
                            <label htmlFor="image" className={styles.uploadLabel}>
                              <Upload size={24} />
                              <span>Upload Image</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <div className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          id="is_active"
                          name="is_active"
                          checked={newService.is_active}
                          onChange={handleCheckboxChange}
                        />
                        <label htmlFor="is_active">Active Service</label>
                      </div>
                    </div>
                  </form>
                )}
              </div>

              {/* Modal Footer */}
              <div className={styles.modalFooter}>
                {modalType === "view" && currentService && (
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button className="btn-secondary" onClick={closeModal}>
                      Close
                    </button>
                    <button className="btn-primary" onClick={() => { setModalError(null); setModalType("edit"); }}>
                      Edit Service
                    </button>
                  </div>
                )}
                {modalType === "delete" && currentService && (
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <button className="btn-danger" onClick={handleDeleteService}>
                      Delete Service
                    </button>
                  </div>
                )}
                {modalType === "edit" && currentService && (
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button className="btn-secondary" onClick={closeModal} disabled={isUpdating}>
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={handleUpdateService}
                      disabled={isUpdating}
                    >
                      {isUpdating ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
                {modalType === "add" && (
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={closeModal}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => handleCreateService()}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Creating..." : "Create Service"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ServiceManagement;